import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  RowData,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useLocalstorageState } from 'rooks'

import Button from '../buttons/button'
import VendiaMenuButton from '../buttons/menu-button'
import Icon from '../icons/icon'
import Checkbox from '../inputs/checkbox.input'
import ListboxInput from '../inputs/listbox.input'

// Tell typescript that header.column.columnDef.meta?.className is ok
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string
  }
}

export type TableProps<T> = {
  data: T[]
  columns: ColumnDef<T, any>[]
  getRowId?: ((originalRow: T, index: number, parent?: Row<T> | undefined) => string) | undefined
  className?: string
  loading?: boolean
  getRowLinkTo?: (row: Row<T>) => { to: string; state: { search: string } }
  isRowLinkEnabled?: ({ row, cell }: { row: Row<T>; cell: Cell<T, any> }) => boolean
  isRowDisabled?: ({ row, cell }: { row: Row<T>; cell: Cell<T, any> }) => boolean
  rowProps?: (row: Row<T>) => any
  rowDividers?: boolean
  paginationConfig?: {
    hide?: boolean
    pageSize?: number
    onPageChange?: () => void
    canSetPageSize?: boolean
    canGetNextPage?: () => boolean
    manualPagination?: boolean
  }
  initialColumnVisibility?: VisibilityState
  canSetColumnVisibility?: boolean
  columnVisibilityStorageKey?: string
  customEmptyMessage?: string
  initialSorting?: SortingState
  disableHover?: boolean
}

export const Table = <T extends Record<string, any>>({
  data = [],
  columns = [],
  getRowId,
  className,
  loading,
  getRowLinkTo,
  isRowLinkEnabled,
  isRowDisabled,
  rowProps,
  rowDividers,
  paginationConfig,
  initialColumnVisibility = {},
  canSetColumnVisibility = false,
  columnVisibilityStorageKey = '',
  customEmptyMessage,
  initialSorting = [],
  disableHover = false,
  ...rest
}: TableProps<T>) => {
  if (canSetColumnVisibility && !columnVisibilityStorageKey) {
    throw new Error(
      'columnVisibilityStorageKey is required when canSetColumnVisibility is true: provide a unique key to track column visibility of this table in localStorage',
    )
  }

  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
  })
  const setCurrentPageParam = (page: any) => {
    searchParams.set('page', page)
    setSearchParams(searchParams)
  }
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: Number(searchParams.get('page')) - 1 || 0, // Convert to zero-indexed
    pageSize: paginationConfig?.pageSize || 100,
  })

  // Column visibility state, stored in local storage
  const [columnVisibility, setColumnVisibility] = useLocalstorageState(
    `columnVisibility:${columnVisibilityStorageKey}`,
    initialColumnVisibility,
  )

  const table = useReactTable({
    data,
    columns: columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updater) => {
      if (typeof updater !== 'function') return
      setPagination(updater)
      if (typeof paginationConfig?.onPageChange === 'function') {
        paginationConfig.onPageChange()
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      pagination,
      columnVisibility,
    },
    initialState: {
      sorting: initialSorting,
    },
    manualPagination: paginationConfig?.manualPagination === true ? true : false,
    autoResetPageIndex: false,
  })

  // Override table.getCanNextPage for custom checks (nextToken, etc)
  const getCanNextPage = () => {
    if (paginationConfig?.canGetNextPage !== undefined) {
      return table.getCanNextPage() || paginationConfig?.canGetNextPage()
    } else {
      return table.getCanNextPage()
    }
  }
  const currentPageIndex = table?.getState()?.pagination?.pageIndex

  return (
    <>
      <div className='grid flex-1 grid-cols-1 overflow-x-auto'>
        <table className={`${className} w-full border-collapse border-none text-sm`} {...rest}>
          <thead className='border-b'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className='text-left'>
                {headerGroup.headers.map((header) => {
                  const columnDirectionIcon =
                    header.column.getIsSorted() === 'desc' ? (
                      <Icon name='lucide-arrow-down' size={16} className={'!block w-4'} />
                    ) : (
                      <Icon name='lucide-arrow-up' size={16} className={'!block w-4'} />
                    )
                  const unsortedColIcon = header.column.getCanSort() ? (
                    <Icon name='lucide-chevrons-up-down' size={16} className={'text-faint !block w-4'} />
                  ) : (
                    ''
                  )
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`text-light px-4 py-0 font-normal first:pl-6 last:pr-6 ${header.column.columnDef.meta?.className || ''}`}
                    >
                      <div
                        {...{
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                        className={`inline-flex items-center py-3 ${
                          header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                        } ${header.column.getIsSorted() ? 'text-dark' : ''}`}
                      >
                        <span className='font-bold'>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        {header.column.getCanSort() && (
                          <span title={header.column.getCanSort() ? 'Sort' : ''} className='ml-1' data-ignore-padding>
                            {header.column.getIsSorted() ? columnDirectionIcon : unsortedColIcon}
                          </span>
                        )}
                      </div>
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody className='bg-white'>
            {table.getRowModel().rows.map((row) => {
              const thisRowProps = rowProps ? rowProps(row as any) : {}
              const firstCell = row.getVisibleCells()[0]
              const isDisabled = isRowDisabled ? isRowDisabled({ row, cell: firstCell } as any) : false

              return (
                <tr key={row.id} {...thisRowProps} className={`${isDisabled ? 'bg-slate-200' : disableHover ? '' : 'hover:bg-uibg-2'}`}>
                  {row.getVisibleCells().map((cell) => {
                    const rowLinkTo =
                      getRowLinkTo &&
                      !isDisabled &&
                      (isRowLinkEnabled?.({ row, cell } as any) ?? true) &&
                      getRowLinkTo(row as any)
                    return (
                      <td
                        className={`group whitespace-pre transition duration-100 ease-in-out ${
                          rowDividers ? 'border-b' : ''
                        }`}
                        key={cell.id}
                      >
                        {rowLinkTo ? (
                          <Link
                            to={rowLinkTo.to}
                            state={rowLinkTo.state}
                            className={`flex p-4 pr-0 group-first:pl-6 group-last:pr-6`}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Link>
                        ) : (
                          <div className={`p-4 pr-0 group-first:pl-6 group-last:pr-6`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {table.getRowModel().rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={columns.length} className='p-4 text-center text-gray-500'>
                  {customEmptyMessage ?? 'No data available'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {paginationConfig?.hide ? null : (
        <div className='flex items-center justify-end py-3 pr-4'>
          {paginationConfig?.canSetPageSize ? (
            <ListboxInput
              className='mr-2 min-w-28'
              name='pageSize'
              label='Page Size'
              options={[
                {
                  value: String(paginationConfig.pageSize) || '25',
                  label: String(paginationConfig.pageSize) || '25',
                },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
              value={String(table?.getState()?.pagination?.pageSize)}
              onChange={(option) => {
                table?.setPageSize(Number(option?.value))
              }}
              useNestedLabel
            />
          ) : null}
          {/* Hardcoded height and width to prevent size shift during page loading */}
          <div className='flex h-6 w-20 items-center justify-center'>
            {loading ? (
              <Icon isSpinning name='refresh' size='xs' className='text-gray-400 transition-opacity' />
            ) : (
              <div className='flex items-center text-sm'>
                <Button
                  className='!p-0'
                  icon='lucide-chevron-left'
                  kind='icon'
                  disabled={!table?.getCanPreviousPage() || loading}
                  onClick={() => {
                    table.setPageIndex(currentPageIndex - 1)
                    setCurrentPageParam(currentPageIndex) // Off by one
                  }}
                  aria-label='Previous Page'
                />
                {currentPageIndex + 1}
                <Button
                  className='!p-0'
                  icon='lucide-chevron-right'
                  kind='icon'
                  disabled={!getCanNextPage() || loading}
                  onClick={() => {
                    table.setPageIndex(currentPageIndex + 1)
                    setCurrentPageParam(currentPageIndex + 2) // Off by one
                  }}
                  aria-label='Next Page'
                />
              </div>
            )}
          </div>
          {canSetColumnVisibility ? (
            <VendiaMenuButton
              asChild
              sideOffset={5}
              menuItemContent={
                <div className='flex flex-col gap-2 p-2'>
                  <h6 className='mb-2 text-sm font-bold'>Column visibility</h6>
                  {table
                    .getAllLeafColumns()
                    .filter((c) => c.getCanHide())
                    .map((column) => {
                      return (
                        <div key={column.id} className='w-full'>
                          <Checkbox
                            name={`toggle-visible-${column.id}`}
                            label={
                              <span>
                                {/* Use nice display name if defined in columnDef.header else fall back to id */}
                                {typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}
                              </span>
                            }
                            wrapperClassName='!mb-0'
                            checked={column.getIsVisible()}
                            onClick={column.getToggleVisibilityHandler()}
                          />
                        </div>
                      )
                    })}
                </div>
              }
            >
              <Button icon='gears' iconSize={22} kind='icon' className='mx-1' />
            </VendiaMenuButton>
          ) : null}
        </div>
      )}
    </>
  )
}

export default Table
